import { VibratoClientContext } from "@/components/dashboard/contexts"
import { ApiError, CallbackPhoneNumber, VibratoClient } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { useContext, useState } from "react"

type VerificationMode = "general" | "callback"

export default function VerifyPhoneNumberModal({
  show,
  setShow,
  onClose,
  onPhoneNumberVerified = null,
  mode = "general"
}: {
  show: boolean
  setShow: (show: boolean) => void
  onClose: () => void
  onPhoneNumberVerified: ((verifiedNumber: string) => Promise<void>) | null
  mode?: VerificationMode
}) {
  const [step, setStep] = useState<1 | 2>(1)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [callbackPhoneNumber, setCallbackPhoneNumber] = useState<CallbackPhoneNumber | null>(null)
  const [confirmationCode, setConfirmationCode] = useState("")
  const [message, setMessage] = useState({ text: "", type: "" })
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)

  const vibratoClient: VibratoClient = useContext(VibratoClientContext)

  // Define copy based on mode
  const getModalCopy = () => {
    switch (mode) {
      case "callback":
        return {
          title: "Add Callback Number",
          description:
            "Add a callback number so you can take control of calls when needed. We can call this number when you need to take over a call, when calls connect, or when we need your input during conversations.",
          step2Description: "Enter the confirmation code you received:"
        }
      default:
        return {
          title: "Verify Phone Number",
          description:
            "To continue using Vibrato, you need to verify your phone number. Enter your phone number to receive a confirmation code:",
          step2Description: "Enter the confirmation code you received:"
        }
    }
  }

  const modalCopy = getModalCopy()

  const onSendConfirmation = async () => {
    setIsSendingConfirmation(true)
    setMessage({ text: "", type: "" })
    try {
      const c = await vibratoClient.createCallbackPhoneNumberConfirmation("1", phoneNumber)
      setCallbackPhoneNumber(c)
      setStep(2)
    } catch (error) {
      console.log("Error creating confirmation", error)

      if (error instanceof ApiError && error.data) {
        if (Array.isArray(error.data)) {
          setMessage({ text: error.data.join(", "), type: "error" })
        } else {
          setMessage({ text: `Error creating confirmation: ${error.data.phone_number.join(", ")}`, type: "error" })
        }
      }
    }
    setIsSendingConfirmation(false)

    console.log("Sending confirmation code to", phoneNumber)
    console.log(message, step)
  }

  const onSubmitConfirmation = async (code: string) => {
    setIsConfirming(true)
    setMessage({ text: "", type: "" })

    if (!callbackPhoneNumber) {
      throw new Error("No callback phone number to confirm")
    }

    try {
      await vibratoClient.confirmCallbackPhoneNumber(callbackPhoneNumber.uuid, code)
      if (onPhoneNumberVerified) {
        onPhoneNumberVerified(phoneNumber)
      }
      setIsConfirmed(true)
    } catch (error) {
      if (error instanceof ApiError && error.data) {
        if (Array.isArray(error.data)) {
          setMessage({ text: error.data.join(", "), type: "error" })
        } else {
          setMessage({ text: `Error confirming phone number. Please try again.`, type: "error" })
          console.error("Error confirming phone number", error)
        }

        setIsConfirming(false)
        return
      }
    }

    const successMessage = mode === "callback" ? "Callback number added successfully." : "Phone number confirmed."

    setMessage({ text: successMessage, type: "success" })
    setIsConfirming(false)

    await new Promise((resolve) => setTimeout(resolve, 2000))
    onClose()
    setShow(false)
  }

  const onResendConfirmation = async () => {
    setIsSendingConfirmation(true)
    setMessage({ text: "", type: "" })
    try {
      const c = await vibratoClient.createCallbackPhoneNumberConfirmation("1", phoneNumber)
      setCallbackPhoneNumber(c)
      setMessage({ text: "Confirmation code resent. Please check your messages.", type: "success" })
    } catch (error) {
      console.log("Error resending confirmation", error)

      if (error instanceof ApiError && error.data) {
        if (Array.isArray(error.data)) {
          setMessage({ text: error.data.join(", "), type: "error" })
        } else {
          setMessage({ text: `Error resending confirmation: ${error.data.phone_number.join(", ")}`, type: "error" })
        }
      }
    }
    setIsSendingConfirmation(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmitConfirmation(confirmationCode)
  }

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-purple-600">{modalCopy.title}</span>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col">
          <div className="w-full flex justify-center">
            <img className="px-8 mb-10 h-[50px]" src="/vibrato-logo-alt.svg" />
          </div>

          {step === 1 && (
            <div className="flex flex-col gap-2">
              <p className="text-gray-700 leading-relaxed">{modalCopy.description}</p>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mt-4">
                Phone Number (US-only)
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                placeholder="123-456-7890"
                required
              />
              {message.text && (
                <div
                  className={`mt-3 text-sm font-medium rounded-md ${
                    message.type === "success" ? "text-green-500" : "text-red-700"
                  }`}
                >
                  {message.text}
                </div>
              )}
            </div>
          )}
          {step === 2 && (
            <form onSubmit={handleSubmit}>
              <p className="text-gray-700 mb-4">{modalCopy.step2Description}</p>
              <input
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="mt-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                placeholder="Enter confirmation code"
              />
              {message.text && (
                <div
                  className={`mt-3 text-sm font-medium rounded-md ${
                    message.type === "success" ? "text-green-500" : "text-red-700"
                  }`}
                >
                  {message.text}
                </div>
              )}
            </form>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {!isConfirmed && (
          <>
            {step === 1 && (
              <button
                type="button"
                onClick={onSendConfirmation}
                disabled={isSendingConfirmation}
                className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
              >
                Send Confirmation Code
              </button>
            )}
            {step === 2 && (
              <div>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isConfirming}
                  className="mt-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-fit"
                >
                  {isConfirming ? "Confirming..." : "Submit Confirmation"}
                </button>
                <button
                  type="button"
                  onClick={onResendConfirmation}
                  disabled={isSendingConfirmation}
                  className="mt-2 ml-2 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded w-fit"
                >
                  Resend Code
                </button>
              </div>
            )}
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}
